<template>
  <div
    class="component-event-item"
    :class="{ 'cursor-pointer': cursorPointer }"
  >
    <div
      class="event-card"
      :role="dateId && securityHash ? 'button' : undefined"
      :tabindex="dateId && securityHash ? 0 : undefined"
      @click="onCardClick"
      @keydown.enter.prevent="onCardClick"
      @keydown.space.prevent="onCardClick"
    >
      <!-- Image thumbnail -->
      <div class="event-card-image-wrapper">
        <div
          class="event-card-image"
          :style="{ backgroundImage: `url('${eventImage}')` }"
        >
          <RoundIcon
            v-if="activityIcon"
            :size="28"
            :border-size="0"
            :icon-class="activityIcon"
            class="event-card-sport-badge"
          />
        </div>
      </div>

      <!-- Content -->
      <div class="event-card-content">
        <div>
          <div class="event-card-header">
            <h2 v-if="showName" class="event-card-title">
              {{ name }}
            </h2>
            <!-- Spots badge -->
            <span
              v-if="showAttendance"
              class="event-card-spots-badge"
              :class="spotsBadgeClass"
            >
              <i class="fal fa-users event-card-spots-icon" />
              <template v-if="isVol">Vol</template>
              <template v-else-if="previewEvent">
                max. {{ maximumParticipants }}
              </template>
              <template
                v-else-if="eventIsFinished || eventIsCancelled || noUpcoming"
              >
                {{ displayParticipants }}/{{ maximumParticipants }}
              </template>
              <template v-else>
                {{ spotsLeft }}
              </template>
            </span>
          </div>
          <div v-if="groupName" class="event-card-group">
            <i class="fal fa-users text-xs" />
            <span>{{ groupName }}</span>
          </div>
        </div>

        <div class="event-card-meta">
          <div class="event-card-meta-row">
            <i class="fal fa-clock event-card-meta-icon" />
            <span v-if="previewEvent">
              <template v-if="endDate && recurringMapped">
                {{ date }} {{ startTime }}-{{ endTime }} ({{
                  recurringMapped
                }}
                tot {{ endDate }})
              </template>
              <template v-else>
                {{ date }} {{ startTime }}-{{ endTime }}
              </template>
            </span>
            <span
              v-else-if="
                dateId === null &&
                date === null &&
                (startTime === null || endTime === null)
              "
            >
              <em>Geen events gepland</em>
            </span>
            <span
              v-else-if="
                (date === null && (startTime === null || endTime === null)) ||
                eventIsFinished
              "
            >
              <em>afgelopen</em>
            </span>
            <span v-else-if="eventIsCancelled">Geannuleerd</span>
            <span v-else-if="isProposal">Datum in overleg</span>
            <span v-else>{{ date }} {{ startTime }}-{{ endTime }}</span>
          </div>
          <div class="event-card-meta-row">
            <i class="fal fa-building event-card-meta-icon" />
            <span class="truncate">{{ location }}</span>
          </div>
        </div>

        <div class="event-card-footer">
          <div class="event-card-price">
            <i class="fal fa-euro-sign event-card-meta-icon" />
            <span
              class="event-card-price-badge"
              :class="
                cost > 0 ? 'event-card-price-paid' : 'event-card-price-free'
              "
            >
              <template v-if="cost > 0">{{ $n(cost, 'currency') }}</template>
              <template v-else>Gratis</template>
            </span>
          </div>
          <button
            v-if="showCta"
            type="button"
            class="event-card-cta"
            :class="ctaClass"
            @click.stop="onCtaClick"
          >
            {{ ctaLabel }}
          </button>
        </div>
      </div>
    </div>

    <button
      v-if="buttonText !== null"
      class="btn btn-green btn-block btn-sm mt-2"
    >
      {{ buttonText }}
    </button>
    <div
      v-if="locationFull !== null && !eventIsCancelled"
      class="flex mt-3 text-sm items-center"
    >
      <div class="mr-2">
        <RoundIcon :size="24" icon-class="fal fa-map-marker-alt" />
      </div>
      <a
        v-if="locationLatLng !== null"
        :href="
          'https://www.google.com/maps/search/?api=1&query=' + locationLatLng
        "
        target="_blank"
        class="location-full-text"
      >
        {{ locationFull }}
      </a>
      <span v-else class="location-full-text">{{ locationFull }}</span>
    </div>
  </div>
</template>

<script>
import RoundIcon from './RoundIcon'
import { DateTime } from 'luxon'
import data from '@/utils/data'
import { formatWeekdays } from '@/utils/recurring'

export default {
  name: 'EventItem',
  components: { RoundIcon },
  props: {
    name: {
      type: String,
      required: true
    },
    date: {
      type: String,
      required: false,
      default: ''
    },
    startTime: {
      type: String,
      required: false,
      default: ''
    },
    endTime: {
      type: String,
      required: false,
      default: ''
    },
    location: {
      type: String,
      required: true
    },
    cost: {
      type: String,
      required: true
    },
    maximumParticipants: {
      type: Number,
      required: true
    },
    numberOfParticipants: {
      type: Number,
      required: false,
      default: 0
    },
    activityIcon: {
      type: String,
      required: false,
      default: ''
    },
    eventImage: {
      type: String,
      required: false,
      default: ''
    },
    locationFull: {
      type: String,
      required: false,
      default: null
    },
    locationLatLng: {
      type: String,
      required: false,
      default: null
    },
    showName: {
      type: Boolean,
      required: false,
      default: true
    },
    buttonText: {
      type: String,
      required: false,
      default: null
    },
    cursorPointer: {
      type: Boolean,
      required: false,
      default: false
    },
    numOfNotFullEvents: {
      type: Number,
      required: false,
      default: 0
    },
    finishedNumberOfParticipants: {
      type: Number,
      required: false,
      default: 0
    },
    origin: {
      type: String,
      required: false,
      default: ''
    },
    dateId: {
      type: Number,
      required: false,
      default: null
    },
    previewEvent: {
      type: Boolean,
      required: false,
      default: false
    },
    endDate: {
      type: String,
      required: false,
      default: ''
    },
    recurring: {
      type: String,
      required: false,
      default: ''
    },
    recurringWeekdays: {
      type: Array,
      required: false,
      default: null
    },
    isEnded: {
      type: Boolean,
      required: false,
      default: false
    },
    privateEvent: {
      type: Boolean,
      required: false,
      default: false
    },
    eventIsCancelled: {
      type: Boolean,
      required: false,
      default: false
    },
    showAttendance: {
      type: Boolean,
      required: false,
      default: true
    },
    isProposal: {
      type: Boolean,
      required: false,
      default: false
    },
    groupId: {
      type: Number,
      required: false,
      default: null
    },
    groupName: {
      type: String,
      required: false,
      default: ''
    },
    securityHash: {
      type: String,
      required: false,
      default: ''
    },
    isSignedUp: {
      type: Boolean,
      required: false,
      default: false
    },
    isWaitListed: {
      type: Boolean,
      required: false,
      default: false
    },
    showCtaButton: {
      type: Boolean,
      required: false,
      default: false
    },
    waitlistEnabled: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  computed: {
    recurringMapped: function () {
      if (
        this.recurring === '7,day' &&
        this.recurringWeekdays &&
        this.recurringWeekdays.length > 1
      ) {
        return `elke ${formatWeekdays(this.recurringWeekdays)}`
      }
      return data.formatRecurring(this.recurring)
    },
    eventIsFinished: function () {
      return DateTime.local() > DateTime.fromISO(this.endDate)
    },
    hasParticipantLimit: function () {
      return this.maximumParticipants > 0
    },
    spotsLeft: function () {
      if (!this.hasParticipantLimit) return null
      return this.maximumParticipants - this.numberOfParticipants
    },
    spotsRatio: function () {
      if (!this.hasParticipantLimit) return 0
      return this.numberOfParticipants / this.maximumParticipants
    },
    isVol: function () {
      return (
        this.hasParticipantLimit &&
        this.numberOfParticipants >= this.maximumParticipants
      )
    },
    noUpcoming: function () {
      return (
        this.origin !== '/event-list-own' &&
        !this.date &&
        (!this.startTime || !this.endTime)
      )
    },
    displayParticipants: function () {
      return this.eventIsFinished || this.noUpcoming
        ? this.finishedNumberOfParticipants
        : this.numberOfParticipants
    },
    spotsBadgeClass: function () {
      if (this.isVol) return 'event-card-spots-full'
      if (this.spotsRatio >= 0.8) return 'event-card-spots-almost'
      return 'event-card-spots-available'
    },
    showCta: function () {
      if (
        !this.showCtaButton ||
        this.previewEvent ||
        this.eventIsCancelled ||
        this.isEnded ||
        this.eventIsFinished ||
        !this.date ||
        !this.startTime ||
        !this.endTime
      ) {
        return false
      }
      // Hide CTA when event is full and waitlist is not enabled (and user is not already signed up/waitlisted)
      if (
        this.isVol &&
        !this.waitlistEnabled &&
        !this.isSignedUp &&
        !this.isWaitListed
      ) {
        return false
      }
      return true
    },
    ctaLabel: function () {
      if (this.isSignedUp) return 'Aangemeld \u2713'
      if (this.isWaitListed) return 'Op wachtlijst \u2713'
      return this.isVol ? 'Wachtlijst' : 'Doe mee'
    },
    ctaClass: function () {
      if (this.isSignedUp) return 'event-card-cta-signed-up'
      if (this.isWaitListed) return 'event-card-cta-waitlisted'
      return this.isVol ? 'event-card-cta-waitlist' : 'event-card-cta-join'
    }
  },
  methods: {
    onCardClick: function () {
      if (this.dateId && this.securityHash) {
        const origin = this.origin || 'events'
        this.$router.push(
          `/events/${this.dateId}/${
            this.securityHash
          }?origin=${encodeURIComponent(origin)}`
        )
      }
    },
    onCtaClick: function () {
      this.onCardClick()
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/css/variables.scss';

.component-event-item {
  margin-bottom: 0.625rem;
}

.event-card {
  display: flex;
  overflow: hidden;
  background: #fff;
  border-radius: 0.75rem;
  border: 1px solid #f3f4f6;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  transition: box-shadow 0.2s, border-color 0.2s;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    border-color: #e5e7eb;
  }
}

.event-card-image-wrapper {
  flex-shrink: 0;
  padding: 0.75rem;
  padding-right: 0;
}

.event-card-image {
  position: relative;
  width: 6rem;
  height: 6rem;
  border-radius: 0.5rem;
  overflow: hidden;
  background-color: #f3f4f6;
  background-size: cover;
  background-position: center;

  @media (min-width: 640px) {
    width: 7rem;
    height: 7rem;
  }
}

.event-card-sport-badge {
  position: absolute;
  bottom: 0.375rem;
  right: 0.375rem;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(4px);
  border-radius: 0.375rem;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.event-card-content {
  flex: 1;
  min-width: 0;
  padding: 0.75rem 1rem;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.event-card-header {
  display: flex !important;
  align-items: flex-start !important;
  justify-content: space-between !important;
  gap: 0.5rem !important;
}

.event-card-title {
  margin: 0;
  font-size: 0.935rem;
  font-weight: 600;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: color 0.2s;

  .event-card:hover & {
    color: $ipGreen;
  }
}

.event-card-spots-badge {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.125rem 0.5rem;
  border-radius: 9999px;
  font-size: 0.74rem;
  font-weight: 500;
  white-space: nowrap;
}

.event-card-spots-icon {
  font-size: 0.7rem;
}

.event-card-spots-available {
  background: #ecfdf5;
  color: #059669;
}

.event-card-spots-almost {
  background: #fffbeb;
  color: #d97706;
}

.event-card-spots-full {
  background: #fef2f2;
  color: #dc2626;
}

.event-card-group {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  margin-top: 0.125rem;
  font-size: 0.76rem;
  color: #9ca3af;
}

.event-card-group-link {
  color: #9ca3af;
  text-decoration: none;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  transition: color 0.15s;

  &:hover {
    color: $ipGreen;
  }
}

.event-card-meta {
  margin-top: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
}

.event-card-meta-row {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.82rem;
  color: #6b7280;

  em {
    font-style: italic;
    color: #9ca3af;
  }
}

.event-card-meta-icon {
  flex-shrink: 0;
  width: 0.875rem;
  font-size: 0.82rem;
  color: #9ca3af;
}

.event-card-footer {
  margin-top: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.event-card-price {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.82rem;
}

.event-card-price-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.0625rem 0.375rem;
  border-radius: 9999px;
  font-size: 0.76rem;
  font-weight: 500;
}

.event-card-price-free {
  background: #ecfdf5;
  color: #059669;
}

.event-card-price-paid {
  background: #f3f4f6;
  color: #4b5563;
}

.event-card-cta {
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.875rem;
  border-radius: 0.5rem;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.15s;
  white-space: nowrap;
  border: none;
  font-family: inherit;
}

.event-card-cta-join {
  background: $ipGreen;
  color: #fff;

  &:hover {
    background: darken($ipGreen, 8%);
  }
}

.event-card-cta-signed-up {
  background: #fff;
  color: $ipGreen;
  border: 1px solid $ipGreen;

  &:hover {
    background: #ecfdf5;
  }
}

.event-card-cta-waitlisted {
  background: #fff;
  color: #d97706;
  border: 1px solid #d97706;

  &:hover {
    background: #fffbeb;
  }
}

.event-card-cta-waitlist {
  background: #fffbeb;
  color: #d97706;

  &:hover {
    background: #fef3c7;
  }
}

.location-full-text {
  color: #6b7280;
  font-size: 0.82rem;
}
</style>
